<template>
  <div>
    <!-- Toolbar -->
    <div class="toolbar">
      <div class="toolbar-left">
        <input
          v-model="search"
          type="text"
          :placeholder="$t('bo.platformSkills.searchSkills')"
          class="filter-input"
          @input="debouncedLoad"
        />
        <select v-model="filterGroupId" @change="loadSkills" class="filter-select">
          <option value="">{{ $t('bo.platformSkills.allGroups') }}</option>
          <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.name }}</option>
        </select>
        <select v-model="filterType" @change="loadSkills" class="filter-select">
          <option value="">{{ $t('bo.platformSkills.allTypes') }}</option>
          <option value="technical">{{ $t('bo.platformSkills.skillType.technical') }}</option>
          <option value="transversal">{{ $t('bo.platformSkills.skillType.transversal') }}</option>
        </select>
        <select v-model="filterLevel" @change="loadSkills" class="filter-select">
          <option value="">{{ $t('bo.platformSkills.allLevels') }}</option>
          <option value="L0">{{ $t('bo.platformSkills.skillLevel.L0') }}</option>
          <option value="L2">{{ $t('bo.platformSkills.skillLevel.L2') }}</option>
          <option value="L3">{{ $t('bo.platformSkills.skillLevel.L3') }}</option>
        </select>
        <select v-model="filterStatus" @change="loadSkills" class="filter-select">
          <option value="active">{{ $t('bo.common.active') }}</option>
          <option value="archived">{{ $t('bo.common.archived') }}</option>
          <option value="">{{ $t('bo.common.all') }}</option>
        </select>
      </div>
      <button @click="showCreate = true" class="btn-primary">{{ $t('bo.platformSkills.newSkill') }}</button>
    </div>

    <!-- Bulk action bar -->
    <div v-if="selectedIds.size > 0" class="bulk-bar">
      <span class="bulk-count">{{ $t('bo.platformSkills.selectedCount', { count: selectedIds.size }) }}</span>
      <button @click="selectedIds = new Set()" class="btn-bulk-cancel">{{ $t('bo.platformSkills.deselectAll') }}</button>
      <button @click="showBulkEdit = true" class="btn-bulk btn-bulk-edit">
        {{ $t('bo.platformSkills.editSelected', { count: selectedIds.size }) }}
      </button>
      <button @click="handleBulkAction('archive')" class="btn-bulk btn-bulk-archive" :disabled="bulkLoading">
        {{ bulkLoading ? '...' : $t('bo.platformSkills.archiveSelected') }}
      </button>
      <button @click="handleBulkAction('activate')" class="btn-bulk btn-bulk-activate" :disabled="bulkLoading">
        {{ bulkLoading ? '...' : $t('bo.platformSkills.activateSelected') }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="state-msg">{{ $t('bo.platformSkills.loadingSkills') }}</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>
    <div v-else-if="skills.length === 0" class="state-msg">{{ $t('bo.platformSkills.noSkillsFound') }}</div>

    <!-- Table -->
    <table v-else class="table">
      <thead>
        <tr>
          <th class="col-check">
            <input
              type="checkbox"
              :checked="allSelected"
              :indeterminate.prop="someSelected"
              @change="toggleSelectAll"
            />
          </th>
          <th>{{ $t('bo.platformSkills.colName') }}</th>
          <th>{{ $t('bo.platformSkills.colSlug') }}</th>
          <th>{{ $t('bo.platformSkills.colGroup') }}</th>
          <th>{{ $t('bo.platformSkills.colType') }}</th>
          <th>{{ $t('bo.platformSkills.colLevel') }}</th>
          <th>{{ $t('bo.platformSkills.colKind') }}</th>
          <th>{{ $t('bo.platformSkills.colEsco') }}</th>
          <th>{{ $t('bo.platformSkills.colStatus') }}</th>
          <th>{{ $t('bo.platformSkills.colActions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="skill in skills" :key="skill.id" :class="{ selected: selectedIds.has(skill.id) }">
          <td class="col-check">
            <input
              type="checkbox"
              :checked="selectedIds.has(skill.id)"
              @change="toggleSelect(skill.id)"
            />
          </td>
          <td class="col-name">
            <div class="name-cell">
              <img
                v-if="skill.atom_kind === 'tool' && skill.icon_url"
                :src="skill.icon_url"
                alt=""
                class="tool-icon"
              />
              <div>
                <div class="name">{{ skill.name }}</div>
                <!-- Nom francais (skills_i18n), ou mention a completer. -->
                <div v-if="skill.translations?.fr?.name" class="name-fr">FR · {{ skill.translations.fr.name }}</div>
                <div v-else class="name-fr missing">{{ $t('bo.platformSkills.translation.missing') }}</div>
                <div v-if="skill.aliases?.length" class="aliases">
                  {{ skill.aliases.slice(0, 3).join(', ') }}
                  <span v-if="skill.aliases.length > 3">+{{ skill.aliases.length - 3 }}</span>
                </div>
              </div>
            </div>
          </td>
          <td><code class="slug">{{ skill.slug || '—' }}</code></td>
          <td>{{ skill.skill_groups?.map(g => g.name).join(', ') || '—' }}</td>
          <td>{{ skill.skill_type || '—' }}</td>
          <td>
            <span v-if="skill.level" :class="['badge', `badge-level-${skill.level}`]">{{ skill.level }}</span>
            <span v-else>—</span>
          </td>
          <td>
            <span v-if="skill.atom_kind" :class="['badge', `badge-kind-${skill.atom_kind}`]">{{ skill.atom_kind }}</span>
            <span v-else>—</span>
          </td>
          <td>{{ (skill.esco_mappings || []).length }}</td>
          <td>
            <span :class="['badge', `badge-${skill.status}`]">{{ skill.status }}</span>
          </td>
          <td class="actions">
            <button @click="openEdit(skill)" class="btn-action btn-edit">{{ $t('bo.common.edit') }}</button>
            <button
              v-if="skill.status === 'active'"
              @click="handleArchive(skill)"
              class="btn-action btn-archive"
              :disabled="actionLoading === skill.id"
            >{{ $t('bo.common.archive') }}</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination info -->
    <div v-if="meta.total" class="pagination-info">
      {{ $t('bo.platformSkills.showingOf', { count: skills.length, total: meta.total }) }}
      <button
        v-if="meta.current_page < meta.last_page"
        @click="loadMore"
        class="btn-load-more"
        :disabled="loadingMore"
      >{{ loadingMore ? $t('bo.common.loading') : $t('bo.common.loadMore') }}</button>
    </div>

    <!-- Modals -->
    <CreateSkillModal
      v-if="showCreate"
      :groups="groups"
      @close="showCreate = false"
      @created="handleCreated"
    />

    <EditSkillModal
      v-if="editingSkill"
      :skill="editingSkill"
      :groups="groups"
      @close="editingSkill = null"
      @updated="handleUpdated"
    />

    <BulkEditSkillModal
      v-if="showBulkEdit"
      :ids="[...selectedIds]"
      :groups="groups"
      @close="showBulkEdit = false"
      @updated="handleBulkUpdated"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import platformSkillService from '@/services/platformSkillService'
import CreateSkillModal from './CreateSkillModal.vue'
import EditSkillModal from './EditSkillModal.vue'
import BulkEditSkillModal from './BulkEditSkillModal.vue'

const { t } = useI18n()

const skills = ref([])
const groups = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const error = ref('')
const filterGroupId = ref('')
const filterType = ref('')
const filterLevel = ref('')
const filterStatus = ref('active')
const search = ref('')
const actionLoading = ref(null)
const showCreate = ref(false)
const editingSkill = ref(null)
const meta = ref({})
const selectedIds = ref(new Set())
const bulkLoading = ref(false)
const showBulkEdit = ref(false)

const allSelected = computed(() =>
  skills.value.length > 0 && skills.value.every(s => selectedIds.value.has(s.id))
)
const someSelected = computed(() =>
  selectedIds.value.size > 0 && !allSelected.value
)

const toggleSelectAll = () => {
  const next = new Set(selectedIds.value)
  if (allSelected.value) {
    skills.value.forEach(s => next.delete(s.id))
  } else {
    skills.value.forEach(s => next.add(s.id))
  }
  selectedIds.value = next
}

const toggleSelect = (id) => {
  const next = new Set(selectedIds.value)
  next.has(id) ? next.delete(id) : next.add(id)
  selectedIds.value = next
}

let searchTimer = null
const debouncedLoad = () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(loadSkills, 350)
}

const buildParams = (page = 1) => {
  const params = { page }
  if (filterStatus.value) params.status = filterStatus.value
  if (filterGroupId.value) params.skill_group_id = filterGroupId.value
  if (filterType.value) params.skill_type = filterType.value
  if (filterLevel.value) params.level = filterLevel.value
  if (search.value) params.search = search.value
  return params
}

const loadSkills = async () => {
  loading.value = true
  error.value = ''
  selectedIds.value = new Set()
  try {
    const res = await platformSkillService.getSkills(buildParams())
    skills.value = res.data || []
    meta.value = res.meta || {}
  } catch (e) {
    error.value = 'Failed to load skills.'
  } finally {
    loading.value = false
  }
}

const loadMore = async () => {
  if (!meta.value.current_page) return
  loadingMore.value = true
  try {
    const res = await platformSkillService.getSkills(buildParams(meta.value.current_page + 1))
    skills.value = [...skills.value, ...(res.data || [])]
    meta.value = res.meta || {}
  } catch (e) {
    alert(e.response?.data?.message || t('bo.platformSkills.failedLoadMore'))
  } finally {
    loadingMore.value = false
  }
}

const loadGroups = async () => {
  try {
    // platform_only : n'afficher que les groupes de la plateforme, pas les groupes ESCO importes.
    const res = await platformSkillService.getGroups({ is_active: 'true', platform_only: true, per_page: 500 })
    groups.value = res.data || []
  } catch (e) {
    // non-critical
  }
}

const openEdit = (skill) => {
  editingSkill.value = skill
}

const handleArchive = async (skill) => {
  if (!confirm(t('bo.platformSkills.confirmArchiveSkill', { name: skill.name }))) return
  actionLoading.value = skill.id
  try {
    await platformSkillService.archiveSkill(skill.id)
    await loadSkills()
  } catch (e) {
    alert(e.response?.data?.message || t('bo.platformSkills.failedArchive'))
  } finally {
    actionLoading.value = null
  }
}

const handleBulkAction = async (action) => {
  const ids = [...selectedIds.value]
  if (!confirm(t('bo.platformSkills.confirmBulkAction', { action, count: ids.length }))) return
  bulkLoading.value = true
  try {
    await platformSkillService.bulkAction(ids, action)
    await loadSkills()
  } catch (e) {
    alert(e.response?.data?.message || t('bo.platformSkills.failedLoadSkills'))
  } finally {
    bulkLoading.value = false
  }
}

const handleBulkUpdated = () => {
  showBulkEdit.value = false
  loadSkills()
}

const handleCreated = () => {
  showCreate.value = false
  loadSkills()
}

const handleUpdated = () => {
  editingSkill.value = null
  loadSkills()
}

defineExpose({ loadSkills })
onMounted(() => {
  loadGroups()
  loadSkills()
})
</script>

<style scoped>
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.toolbar-left {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.filter-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 200px;
}

.filter-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
}

.btn-primary {
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary:hover { background: #2563eb; }

.bulk-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
}

.bulk-count { font-size: 0.875rem; font-weight: 600; color: #1d4ed8; }

.btn-bulk-cancel {
  padding: 0.3rem 0.75rem;
  border: 1px solid #93c5fd;
  background: white;
  color: #1d4ed8;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  cursor: pointer;
}

.btn-bulk {
  padding: 0.3rem 0.875rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-bulk:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-bulk-edit { background: #e0e7ff; color: #3730a3; }
.btn-bulk-edit:hover { background: #c7d2fe; }
.btn-bulk-archive { background: #fef3c7; color: #92400e; }
.btn-bulk-archive:hover:not(:disabled) { background: #fde68a; }
.btn-bulk-activate { background: #d1fae5; color: #065f46; }
.btn-bulk-activate:hover:not(:disabled) { background: #a7f3d0; }

.state-msg {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
}

.error-msg {
  padding: 1rem 1.25rem;
  color: #dc2626;
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #fecaca;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: top;
}

.table tr:last-child td { border-bottom: none; }
.table tr.selected td { background: #eff6ff; }

.col-check { width: 2.5rem; }
.col-name { max-width: 240px; }
.col-name { min-width: 180px; }
.name-cell { display: flex; align-items: center; gap: 0.5rem; }
.tool-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
  flex-shrink: 0;
  border-radius: 0.25rem;
  background: #f9fafb;
}
.name { font-weight: 600; color: #1f2937; }
.aliases { color: #6b7280; font-size: 0.8rem; margin-top: 0.2rem; }

.slug {
  font-size: 0.8rem;
  background: #f3f4f6;
  padding: 0.15rem 0.4rem;
  border-radius: 0.25rem;
  color: #374151;
}

.badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.badge-active   { background: #d1fae5; color: #065f46; }
.badge-archived { background: #f3f4f6; color: #6b7280; }
.badge-kind-skill         { background: #eff6ff; color: #1d4ed8; }
.badge-kind-tool          { background: #f0fdf4; color: #166534; }
.badge-kind-certification { background: #fef3c7; color: #92400e; }
.badge-kind-framework     { background: #faf5ff; color: #7c3aed; }
.badge-level-L0 { background: #f1f5f9; color: #475569; }
.badge-level-L2 { background: #ecfdf5; color: #065f46; }
.badge-level-L3 { background: #fdf4ff; color: #6b21a8; }

.actions { display: flex; gap: 0.5rem; }

.btn-action {
  padding: 0.3rem 0.65rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  cursor: pointer;
  border: 1px solid;
  font-weight: 500;
  transition: all 0.15s;
}

.btn-action:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-edit    { border-color: #3b82f6; color: #3b82f6; background: white; }
.btn-edit:hover:not(:disabled) { background: #eff6ff; }
.btn-archive { border-color: #f59e0b; color: #f59e0b; background: white; }
.btn-archive:hover:not(:disabled) { background: #fffbeb; }

.pagination-info {
  padding: 1rem;
  text-align: center;
  font-size: 0.875rem;
  color: #6b7280;
  background: white;
  border: 1px solid #e5e7eb;
  border-top: none;
  border-radius: 0 0 0.75rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.btn-load-more {
  padding: 0.35rem 0.875rem;
  border: 1px solid #3b82f6;
  border-radius: 0.375rem;
  color: #3b82f6;
  background: white;
  font-size: 0.8rem;
  cursor: pointer;
}

.btn-load-more:hover:not(:disabled) { background: #eff6ff; }
.btn-load-more:disabled { opacity: 0.5; cursor: not-allowed; }

.name-fr { color: #4b5563; font-size: 0.8rem; margin-top: 0.15rem; }
.name-fr.missing { color: #b45309; }
</style>
